import { useState } from "react";
import { X, Loader2, Play, Sparkles, Image, Video } from "lucide-react";
import FileUploadField from "@/components/ui/FileUploadField";

import { useAddVideoMutation } from "@/redux/feature/videoGeneration/videoGenerationApi";
import SimpleVideoPlayer from "./SimpleVideoPlayer";
import MinimalFileUpload from "@/components/ui/MinimalFileUpload";

interface TestFeatureModalProps {
  setOpen: (open: boolean) => void;
}

const TestFeatureModal = ({ setOpen }: TestFeatureModalProps) => {
  const [prompt, setPrompt] = useState("");
  const [productImageUrl, setProductImageUrl] = useState("");
  const [referenceVideoUrl, setReferenceVideoUrl] = useState("");
  const [uploadKey, setUploadKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const [addVideo, { isLoading: isGenerating }] = useAddVideoMutation();

  const handleImageUpload = (files: any[]) => {
    const url = files?.[0]?.live_url ?? "";
    setProductImageUrl(url);
  };

  const handleVideoUpload = (files: any[]) => {
    const url = files?.[0]?.live_url ?? "";
    setReferenceVideoUrl(url);
    setError(null);
  };

  const handleGenerate = async () => {
    if (!prompt.trim() || !productImageUrl || !referenceVideoUrl) return;
    setError(null);

    try {
      const payload = {
        reference_video_url: referenceVideoUrl,
        product_image_url: productImageUrl,
        prompt_brief: prompt,
      };

      const response = await addVideo(payload).unwrap();

      if (response?.job_id) {
        setJobId(response.job_id);
      } else {
        throw new Error("No job_id returned from API");
      }
    } catch (err: any) {
      setError("Failed to start video generation. Please try again.");
      console.error("Video generation error:", err);
    }
  };

  const handleNewGeneration = () => {
    setJobId(null);
    setPrompt("");
    setProductImageUrl("");
    setReferenceVideoUrl("");
    setUploadKey((prev) => prev + 1);
    setError(null);
  };

  const isFormValid = Boolean(
    prompt.trim() && productImageUrl && referenceVideoUrl
  );

  // Show video player if jobId exists
  if (jobId) {
    return (
      <div className="relative">
        <button
          onClick={handleNewGeneration}
          className="absolute top-6 right-6 z-10 px-5 py-2.5 bg-white text-gray-700 text-sm font-medium rounded-lg shadow-md hover:shadow-lg hover:bg-gray-50 transition-all flex items-center gap-2 border border-gray-200"
        >
          <X className="w-4 h-4" />
          New Generation
        </button>
        <SimpleVideoPlayer
          jobId={jobId}
          inputPrompt={prompt}
          productImageUrl={productImageUrl}
          referenceVideoUrl={referenceVideoUrl}
        />
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="flex items-center justify-between pb-6 border-b border-gray-200">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Video Generator
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Create AI-powered product videos
          </p>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="px-8 py-6 max-h-[calc(100vh-180px)] overflow-y-auto">
        <div className="space-y-6 max-w-3xl">
          {/* Prompt Section */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Sparkles className="w-4 h-4 text-gray-400" />
              Video Prompt
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe how you want your product to be presented in the video..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none text-gray-900 placeholder:text-gray-400 transition-shadow"
            />
            <p className="text-xs text-gray-500 mt-1.5">
              Be specific about actions, angles, and desired outcomes
            </p>
          </div>

          {/* Upload Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Product Image Upload */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Image className="w-4 h-4 text-gray-400" />
                Product Image
              </label>
              <MinimalFileUpload
                onFilesUploaded={handleImageUpload}
                maxFiles={1}
                maxSize={10 * 1024 * 1024}
                accept={{ "image/*": [".jpg", ".jpeg", ".png", ".webp"] }}
                identifier="product-image"
                key={`image-upload-${uploadKey}`}
              />
              {productImageUrl && (
                <div className="mt-3 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                  <img
                    src={productImageUrl}
                    alt="Product preview"
                    className="w-full h-44 object-cover"
                  />
                </div>
              )}
            </div>

            {/* Reference Video Upload */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Video className="w-4 h-4 text-gray-400" />
                Reference Video
              </label>
              <MinimalFileUpload
                onFilesUploaded={handleVideoUpload}
                maxFiles={1}
                maxSize={500 * 1024 * 1024}
                accept={{ "video/*": [".mp4", ".mov", ".avi", ".webm"] }}
                identifier="reference-video"
                key={`video-upload-${uploadKey}`}
              />
              {referenceVideoUrl && (
                <div className="mt-3 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                  <video
                    src={referenceVideoUrl}
                    controls
                    className="w-full h-44 object-cover"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <X className="w-3 h-3 text-red-600" />
              </div>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between px-8 py-5 border-t border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-500">
          {isFormValid ? "Ready to generate" : "Fill all fields to continue"}
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={!isFormValid || isGenerating}
            className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 shadow-sm hover:shadow"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Generate Video
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestFeatureModal;
